import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag, Wrench, CalendarDays, Phone, User, Signal } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";

const TABS = [
  { value: "reservations", label: "Réservations", icon: ShoppingBag },
  { value: "repairs", label: "Devis réparation", icon: Wrench },
  { value: "appointments", label: "Rendez-vous", icon: CalendarDays },
  { value: "plans", label: "Forfaits", icon: Signal },
];

const STATUS_CONFIG = {
  reservations: [
    { value: "new", label: "Nouvelle", cls: "bg-yellow-100 text-yellow-800" },
    { value: "contacted", label: "Contacté", cls: "bg-blue-100 text-blue-700" },
    { value: "done", label: "Terminée", cls: "bg-emerald-100 text-emerald-700" },
    { value: "cancelled", label: "Annulée", cls: "bg-slate-200 text-slate-600" },
  ],
  repairs: [
    { value: "new", label: "Nouvelle", cls: "bg-yellow-100 text-yellow-800" },
    { value: "contacted", label: "Contacté", cls: "bg-blue-100 text-blue-700" },
    { value: "received", label: "Reçu", cls: "bg-orange-100 text-orange-700" },
    { value: "done", label: "Terminée", cls: "bg-emerald-100 text-emerald-700" },
    { value: "cancelled", label: "Annulée", cls: "bg-slate-200 text-slate-600" },
  ],
  appointments: [
    { value: "new", label: "Nouvelle", cls: "bg-yellow-100 text-yellow-800" },
    { value: "confirmed", label: "Confirmé", cls: "bg-blue-100 text-blue-700" },
    { value: "done", label: "Terminé", cls: "bg-emerald-100 text-emerald-700" },
    { value: "cancelled", label: "Annulé", cls: "bg-slate-200 text-slate-600" },
  ],
  plans: [
    { value: "new", label: "Nouvelle", cls: "bg-yellow-100 text-yellow-800" },
    { value: "contacted", label: "Contacté", cls: "bg-blue-100 text-blue-700" },
    { value: "done", label: "Souscrit", cls: "bg-emerald-100 text-emerald-700" },
    { value: "cancelled", label: "Annulée", cls: "bg-slate-200 text-slate-600" },
  ],
};

const estimateText = (est) => {
  if (!est || est.price == null) return "Devis après diagnostic";
  return est.type === "fixed" ? `${est.price} €` : `À partir de ${est.price} €`;
};

export default function AdminRequests() {
  const [tab, setTab] = useState("reservations");
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    setItems(null);
    api.get(`/admin/requests/${tab}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (item, status) => {
    try {
      await api.patch(`/admin/requests/${tab}/${item.id}`, { status });
      toast.success("Statut mis à jour");
      setItems((list) => list.map((x) => (x.id === item.id ? { ...x, status } : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusOf = (item) => STATUS_CONFIG[tab].find((s) => s.value === item.status) || STATUS_CONFIG[tab][0];

  return (
    <div data-testid="admin-requests-page">
      <h1 className="font-display text-2xl font-extrabold text-slate-900">Demandes clients</h1>

      <div className="mt-5 flex gap-2 overflow-x-auto pb-1" data-testid="requests-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.value} data-testid={`requests-tab-${t.value}`} onClick={() => setTab(t.value)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${tab === t.value ? "bg-ink text-gold" : "bg-white border border-slate-200 text-slate-600"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {items === null ? (
        <div className="mt-5 space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="requests-empty">
          <p className="font-display font-semibold text-slate-800">Aucune demande</p>
          <p className="mt-1 text-sm text-slate-500">Les nouvelles demandes apparaîtront ici.</p>
        </div>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="requests-list">
          {items.map((item) => {
            const st = statusOf(item);
            return (
              <li key={item.id} data-testid={`request-row-${item.id}`} className="card-oxy p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-bold text-gold-dark">{item.reference}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${st.cls}`} data-testid={`request-status-${item.id}`}>{st.label}</span>
                    </p>
                    {tab === "reservations" && (
                      <p className="mt-1.5 font-display font-bold text-slate-900">{item.product_name} <span className="text-gold-dark">{item.price} €</span></p>
                    )}
                    {tab === "repairs" && (
                      <p className="mt-1.5 font-display font-bold text-slate-900">
                        {item.model_label || item.device_type} — {item.problem_label}
                      </p>
                    )}
                    {tab === "appointments" && (
                      <p className="mt-1.5 font-display font-bold text-slate-900">{item.day} · {item.slot}</p>
                    )}
                    {tab === "plans" && (
                      <p className="mt-1.5 font-display font-bold text-slate-900">
                        {item.plan_name} <span className="text-gold-dark">{item.plan_price} €/mois</span>
                        {item.current_operator && <span className="ml-2 text-xs font-semibold text-slate-500">· depuis {item.current_operator}</span>}
                      </p>
                    )}
                    <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{item.first_name || item.name}</span>
                      <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-gold-dark"><Phone className="h-3.5 w-3.5" />{item.phone}</a>
                      <span>{new Date(item.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                    </p>
                    {tab === "repairs" && (
                      <div className="mt-2 space-y-1.5">
                        <p className="text-sm font-semibold text-emerald-700">{estimateText(item.estimate)}{item.estimate?.duration ? ` · ${item.estimate.duration}` : ""}</p>
                        {Object.entries(item.answers || {}).map(([q, a]) => (
                          <p key={q} className="text-xs text-slate-500">{q} → <strong className="text-slate-700">{a}</strong></p>
                        ))}
                        {(item.photos || []).length > 0 && (
                          <div className="flex gap-2 pt-1">
                            {item.photos.map((p) => (
                              <a key={p} href={fileUrl(p)} target="_blank" rel="noopener noreferrer">
                                <img src={fileUrl(p)} alt="photo panne" className="h-14 w-14 rounded-lg border border-slate-200 object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {tab === "appointments" && item.reason && (
                      <p className="mt-2 text-xs text-slate-500">Motif : {item.reason}</p>
                    )}
                  </div>
                  <select
                    data-testid={`request-status-select-${item.id}`}
                    value={item.status}
                    onChange={(e) => updateStatus(item, e.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 focus:border-gold focus:outline-none"
                  >
                    {STATUS_CONFIG[tab].map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
